import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon, Loader2 } from "lucide-react";
import { format, addDays, isBefore, startOfToday } from "date-fns";
import { cn } from "@/lib/utils";
import { useRescheduleVisit } from "@/hooks/usePatientVisits";

interface Visit {
  id: string;
  visit_type: string;
  visit_number: number;
  scheduled_date: string;
}

interface RescheduleVisitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visit: Visit;
  onSuccess?: () => void;
}

const RESCHEDULE_REASONS = [
  { value: "patient_request", label: "Patient requested different date" },
  { value: "scheduling_conflict", label: "Scheduling conflict" },
  { value: "staff_unavailable", label: "Staff unavailable" },
  { value: "patient_illness", label: "Patient illness" },
  { value: "equipment_unavailable", label: "Equipment unavailable" },
  { value: "site_closure", label: "Site temporarily closed" },
  { value: "transportation", label: "Transportation issues" },
  { value: "other", label: "Other (specify below)" },
] as const;

export function RescheduleVisitDialog({ 
  open, 
  onOpenChange, 
  visit,
  onSuccess 
}: RescheduleVisitDialogProps) {
  const [newDate, setNewDate] = useState<Date | undefined>(
    addDays(new Date(visit.scheduled_date), 7)
  );
  const [reason, setReason] = useState<string>("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const { mutate: reschedule, isPending } = useRescheduleVisit();

  const handleSubmit = () => {
    if (!newDate || !reason) return;
    if (reason === "other" && additionalNotes.trim().length < 5) return;

    const reasonLabel = RESCHEDULE_REASONS.find(r => r.value === reason)?.label || reason;
    const fullNotes = additionalNotes.trim() 
      ? `${reasonLabel}: ${additionalNotes.trim()}`
      : `${reasonLabel} - Rescheduled from ${format(new Date(visit.scheduled_date), 'MMM d, yyyy')}`;

    reschedule(
      {
        visitId: visit.id,
        newDate: format(newDate, 'yyyy-MM-dd'),
        notes: fullNotes
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          setReason("");
          setAdditionalNotes("");
          onSuccess?.();
        }
      }
    );
  };

  const visitLabel = visit.visit_type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  const isValid = newDate && reason && (reason !== "other" || additionalNotes.trim().length >= 5);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Reschedule Visit</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <p className="text-sm text-muted-foreground mb-2">
              Rescheduling <strong>{visitLabel}</strong> (Visit #{visit.visit_number})
            </p>
            <p className="text-sm text-muted-foreground">
              Originally scheduled: {format(new Date(visit.scheduled_date), 'MMMM d, yyyy')}
            </p>
          </div>

          <div className="space-y-2">
            <Label>New Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !newDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {newDate ? format(newDate, 'PPP') : "Select new date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 pointer-events-auto" align="start">
                <Calendar
                  mode="single"
                  selected={newDate}
                  onSelect={setNewDate}
                  disabled={(date) => isBefore(date, startOfToday())}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason for Rescheduling *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger>
                <SelectValue placeholder="Select reason..." />
              </SelectTrigger>
              <SelectContent className="bg-popover border z-50">
                {RESCHEDULE_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">
              Additional Details {reason === "other" ? "*" : "(Optional)"}
            </Label>
            <Textarea
              id="notes"
              placeholder={reason === "other" 
                ? "Please describe the reason for rescheduling..." 
                : "Any additional context about the reschedule..."
              }
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              rows={3}
            />
            {reason === "other" && additionalNotes.trim().length < 5 && (
              <p className="text-xs text-destructive">
                Please provide at least 5 characters when selecting "Other"
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || isPending}>
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Rescheduling...
              </>
            ) : (
              "Reschedule Visit"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
